import { getFirestore, collection, addDoc, Timestamp, query, getDocs, where } from "firebase/firestore";
import { secureGetItem } from "../../../data/utils";

export class NotificationService {
    // NOTIFICA ASSEGNAZIONE
    static async createVariantAssignmentNotification(variantId, brandId, skuModel, sentTo, notifyCurrentUser = false, modelName = "", productId = "") {
        if (!variantId || !sentTo) {
            console.error("Parametri mancanti:", { variantId, sentTo });
            throw new Error("Parametri necessari mancanti");
        }

        try {
            const db = getFirestore();
            const userEmail = secureGetItem("email");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            // Se l'utente corrente è il destinatario e notifyCurrentUser è false, non creare la notifica
            if (userEmail === sentTo && !notifyCurrentUser) {
                console.log(`Notifica NON creata per ${sentTo} perché è l'utente corrente`);
                return null;
            }

            // Crea un nuovo documento nella collezione Notifications
            const notificationData = {
                brandId: brandId || "", // Se brandId non è definito, usa stringa vuota
                date: Timestamp.now(),
                sentTo: sentTo,
                skuModel: skuModel || "",
                type: "Variant assigned",
                variantId: variantId,
                read: false, // Aggiungiamo questo campo per tracciare se la notifica è stata letta
                sentBy: userEmail, // Chi ha assegnato la variante
                nameVariant: modelName || "", // Aggiungiamo il nome della variante/modello
                productId: productId || "" // Aggiungiamo l'ID del prodotto per la navigazione corretta
            };

            // Aggiungi documento alla collezione Notifications
            const notificationRef = await addDoc(collection(db, "Notifications"), notificationData);
            // console.log(`Notifica creata per ${sentTo} con ID: ${notificationRef.id}`);
            return notificationRef.id;
        } catch (error) {
            console.error(`Errore nella creazione della notifica per la variante ${variantId}:`, error);
            throw error;
        }
    }

    // LOGICA NOTIFICHE - CHI RICEVE LE NOTIFICHE PER OGNI STATO:
    // 
    // "Rev. Modellista" → Admin + utente assegnato
    // "Rev. Spaarkly" → ModellerSupervisor + Admin  
    // "Rev. Cliente" → Validator + Cliente (filtrati per brandId)
    // "In Pubblicazione" → Cliente (filtrati per brandId)
    // "Pubblicato" → Admin + Cliente (filtrati per brandId)
    //
    // FLUSSO:
    // 1. getTargetRolesForStatus() → determina i RUOLI destinatari in base al nuovo stato
    // 2. getProfilesWithRoles() → trova gli UTENTI con quei ruoli + aggiunge l'utente assegnato (SOLO per "Rev. Modellista")
    // 3. createVariant*Notifications() → crea notifiche per tutti i destinatari

    // Determina a chi inviare la notifica VALIDAZIONE/DECLINAZIONE in base allo stato della variante
    static getTargetRolesForStatus(newStatus) {
        switch (newStatus) {
            case "Modelist Rev.":
            case "Rev. Modellista":
                return ["Admin"]; // ROLE

            case "Spaarkly Rev.":
            case "Rev. Spaarkly":
                return ["ModellerSupervisor", "Admin"]; // ROLE

            case "Client Rev.":
            case "Rev. Cliente":
                return ["Validator", "Cliente"]; // ROLE

            case "In Publication":
            case "In Pubblicazione":
                return ["Cliente"]; // ROLE

            case "Published":
            case "Pubblicato":
                return ["Admin", "Cliente"]; // ROLE

            default:
                console.warn(`Stato non riconosciuto: "${newStatus}"`);
                return [];
        }
    }

    // Recupera i profili con i ruoli specificati, filtrando i clienti in base al brandId
    static async getProfilesWithRoles(roles, brandId, assignedTo = null, newStatus = null) {
        if (!roles || roles.length === 0) {
            return [];
        }

        try {
            const db = getFirestore();
            const profilesEmails = [];
            const clientEmails = []; // Array per tracciare solo le email dei clienti

            // Esegui una query per ogni ruolo e aggiungi i risultati all'array
            for (const role of roles) {
                // console.log(`🔍 Cercando utenti con ruolo: "${role}" per stato "${newStatus}"`);

                // Usa la collezione corretta "Profiles" (plurale)
                const q = query(collection(db, "Profiles"));
                const querySnapshot = await getDocs(q);

                // Filtra i risultati manualmente per fare un confronto case-insensitive
                const matchingDocs = querySnapshot.docs.filter(doc => {
                    const profileData = doc.data();
                    const hasMatchingRole = profileData.role &&
                        profileData.role.toLowerCase() === role.toLowerCase();

                    // if (hasMatchingRole) {
                    //     console.log(`✅ Trovato utente con ruolo "${profileData.role}": ${profileData._email}`);
                    // }

                    return hasMatchingRole;
                });

                if (role.toLowerCase() === "cliente" && brandId) {
                    // Per i clienti, controlla se il brandId è nella loro lista list_main_brands
                    for (const docSnapshot of matchingDocs) {
                        const profileData = docSnapshot.data();
                        const mainBrands = profileData.list_main_brands || [];

                        // Controlla se il brandId è nella lista list_main_brands del cliente
                        if (mainBrands.includes(brandId) &&
                            profileData._email &&
                            !profilesEmails.includes(profileData._email)) {
                            profilesEmails.push(profileData._email);
                            clientEmails.push(profileData._email); // Aggiungi anche all'array dei clienti
                            // console.log(`📧 Aggiunto cliente: ${profileData._email}`);
                        }
                    }
                } else {
                    // Per tutti gli altri ruoli, aggiungi tutti i profili
                    matchingDocs.forEach((doc) => {
                        const profileData = doc.data();
                        if (profileData._email && !profilesEmails.includes(profileData._email)) {
                            profilesEmails.push(profileData._email);
                            // console.log(`📧 Aggiunto utente con ruolo "${profileData.role}": ${profileData._email}`);
                        }
                    });
                }
            }

            // Aggiungi l'utente assegnato SOLO se lo stato è "Rev. Modellista"
            if (assignedTo &&
                !profilesEmails.includes(assignedTo) &&
                (newStatus === "Rev. Modellista" || newStatus === "Modelist Rev.")) {
                profilesEmails.push(assignedTo);
                // console.log(`👤 Aggiunto utente assegnato alla lista destinatari per stato "Rev. Modellista": ${assignedTo}`);
            } else if (assignedTo && (newStatus === "Rev. Modellista" || newStatus === "Modelist Rev.")) {
                // console.log(`ℹ️ Utente assegnato ${assignedTo} già presente nella lista per stato "Rev. Modellista"`);
            } else if (assignedTo) {
                // console.log(`⚠️ Utente assegnato ${assignedTo} NON aggiunto per stato "${newStatus}" (solo per Rev. Modellista)`);
            }

            // Se "Cliente" è tra i ruoli richiesti, mostriamo un messaggio appropriato
            if (roles.some(role => role.toLowerCase() === "cliente")) {
                if (clientEmails.length > 0) {
                    // console.log(`Clienti che riceveranno la notifica (filtrati per brandId ${brandId}):`, clientEmails);
                } else {
                    // console.log(`Nessun cliente ha il brandId ${brandId} nella propria lista list_main_brands`);
                }
            }

            return profilesEmails;
        } catch (error) {
            console.error("Errore nel recupero dei profili:", error);
            return [];
        }
    }

    // NOTIFICA VALIDAZIONE
    static async createVariantValidationNotifications(variantId, brandId, skuModel, newStatus, notifyCurrentUser = false, modelName = "", productId = "", assignedTo = null) {
        if (!variantId) {
            console.error("variantId mancante");
            throw new Error("variantId è un parametro necessario");
        }

        try {
            const db = getFirestore();
            const userEmail = secureGetItem("email");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            // Determina i ruoli destinatari in base al nuovo stato
            const targetRoles = this.getTargetRolesForStatus(newStatus);
            // console.log(`Ruoli target determinati:`, targetRoles);

            // Se non ci sono ruoli target, esci senza creare notifiche
            if (targetRoles.length === 0) {
                return [];
            }

            // Recupera i profili con i ruoli specificati, filtrando i clienti in base al brandId e includendo l'utente assegnato solo per "Rev. Modellista"
            const targetEmails = await this.getProfilesWithRoles(targetRoles, brandId, assignedTo, newStatus);

            // Se non ci sono destinatari, esci senza creare notifiche
            if (targetEmails.length === 0) {
                return [];
            }

            // console.log(`Destinatari trovati:`, targetEmails);
            // console.log(`Utente corrente:`, userEmail);
            // console.log(`Notifica all'utente corrente:`, notifyCurrentUser ? "Sì" : "No");

            // Crea una notifica per ogni destinatario
            const notificationPromises = targetEmails.map(async (email) => {
                // Non creare notifiche per l'utente che ha validato la variante a meno che non sia richiesto
                if (email === userEmail && !notifyCurrentUser) {
                    // console.log(`Notifica NON creata per ${email} perché è l'utente corrente`);
                    return null;
                }

                const notificationData = {
                    brandId: brandId || "",
                    date: Timestamp.now(),
                    sentTo: email,
                    skuModel: skuModel || "",
                    type: "Variant validated",
                    variantId: variantId,
                    read: false,
                    sentBy: userEmail,
                    newStatus: newStatus, // Aggiungiamo lo stato per fornire più contesto
                    nameVariant: modelName || "", // Aggiungiamo il nome della variante/modello
                    productId: productId || "" // Aggiungiamo l'ID del prodotto per la navigazione corretta
                };

                try {
                    const notificationRef = await addDoc(collection(db, "Notifications"), notificationData);
                    // console.log(`Notifica creata per ${email} con ID: ${notificationRef.id}`);
                    return notificationRef.id;
                } catch (err) {
                    console.error(`Errore nella creazione della notifica per ${email}:`, err);
                    return null;
                }
            });

            // Attendi che tutte le notifiche siano create e filtra i valori nulli
            const notificationIds = (await Promise.all(notificationPromises)).filter(Boolean);
            // console.log(`Numero di notifiche create:`, notificationIds.length);

            return notificationIds;
        } catch (error) {
            console.error(`Errore nella creazione delle notifiche di validazione per la variante ${variantId}:`, error);
            throw error;
        }
    }

    // NOTIFICA DECLINAZIONE
    static async createVariantDeclineNotifications(variantId, brandId, skuModel, newStatus, validationNoteId = "", notifyCurrentUser = false, modelName = "", productId = "", assignedTo = null) {
        if (!variantId || !newStatus) {
            console.error("Parametri mancanti:", { variantId, newStatus });
            throw new Error("Parametri necessari mancanti");
        }

        try {
            const db = getFirestore();
            const userEmail = secureGetItem("email");

            if (!userEmail) {
                console.error("Email utente non trovata");
                throw new Error("Email utente non trovata");
            }

            // Determina i ruoli destinatari in base al nuovo stato - esattamente come per la validazione
            const targetRoles = this.getTargetRolesForStatus(newStatus);
            // console.log(`Ruoli target determinati:`, targetRoles);

            // Se non ci sono ruoli target, esci senza creare notifiche
            if (targetRoles.length === 0) {
                return [];
            }

            // Recupera i profili con i ruoli specificati, filtrando i clienti in base al brandId e includendo l'utente assegnato solo per "Rev. Modellista"
            const targetEmails = await this.getProfilesWithRoles(targetRoles, brandId, assignedTo, newStatus);

            // Se non ci sono destinatari, esci senza creare notifiche
            if (targetEmails.length === 0) {
                return [];
            }

            // console.log(`Destinatari trovati:`, targetEmails);
            // console.log(`Utente corrente:`, userEmail);
            // console.log(`Notifica all'utente corrente:`, notifyCurrentUser ? "Sì" : "No");

            // Crea una notifica per ogni destinatario
            const notificationPromises = targetEmails.map(async (email) => {
                // Non creare notifiche per l'utente che ha declinato la variante a meno che non sia richiesto
                if (email === userEmail && !notifyCurrentUser) {
                    // console.log(`Notifica NON creata per ${email} perché è l'utente corrente`);
                    return null;
                }

                const notificationData = {
                    brandId: brandId || "",
                    date: Timestamp.now(),
                    sentTo: email,
                    skuModel: skuModel || "",
                    type: "Variant declined",
                    variantId: variantId,
                    read: false,
                    sentBy: userEmail,
                    newStatus: newStatus,
                    declineRef: validationNoteId, // Manteniamo il nome del campo originale ma salviamo l'ID della nota
                    nameVariant: modelName || "", // Aggiungiamo il nome della variante/modello
                    productId: productId || "" // Aggiungiamo l'ID del prodotto per la navigazione corretta
                };

                try {
                    const notificationRef = await addDoc(collection(db, "Notifications"), notificationData);
                    // console.log(`Notifica creata per ${email} con ID: ${notificationRef.id}`);
                    return notificationRef.id;
                } catch (err) {
                    console.error(`Errore nella creazione della notifica per ${email}:`, err);
                    return null;
                }
            });

            // Attendi che tutte le notifiche siano create e filtra i valori nulli
            const notificationIds = (await Promise.all(notificationPromises)).filter(Boolean);
            // console.log(`Numero di notifiche create:`, notificationIds.length);

            return notificationIds;
        } catch (error) {
            console.error(`Errore nella creazione delle notifiche di decline per la variante ${variantId}:`, error);
            throw error;
        }
    }

    // Aggiungere questo nuovo metodo alla classe NotificationService
    static async checkNotificationsForUser(email) {
        if (!email) {
            console.error("Email non specificata");
            return;
        }

        try {
            const db = getFirestore();

            // Query per notifiche ricevute
            const receivedQuery = query(
                collection(db, "Notifications"),
                where("sentTo", "==", email)
            );

            // Query per notifiche inviate
            const sentQuery = query(
                collection(db, "Notifications"),
                where("sentBy", "==", email)
            );

            // Esegui entrambe le query
            const [receivedSnapshot, sentSnapshot] = await Promise.all([
                getDocs(receivedQuery),
                getDocs(sentQuery)
            ]);

            const notifsList = [];
            const notificationIds = new Set();

            // Aggiungi notifiche ricevute
            receivedSnapshot.forEach((doc) => {
                if (!notificationIds.has(doc.id)) {
                    notifsList.push({
                        id: doc.id,
                        ...doc.data(),
                        notificationDirection: 'received'
                    });
                    notificationIds.add(doc.id);
                }
            });

            // Aggiungi notifiche inviate
            sentSnapshot.forEach((doc) => {
                if (!notificationIds.has(doc.id)) {
                    notifsList.push({
                        id: doc.id,
                        ...doc.data(),
                        notificationDirection: 'sent'
                    });
                    notificationIds.add(doc.id);
                }
            });

            if (notifsList.length === 0) {
                // console.log(`Nessuna notifica trovata per ${email}`);
                return [];
            }

            // console.log(`Trovate ${notifsList.length} notifiche per ${email} (${receivedSnapshot.size} ricevute, ${sentSnapshot.size} inviate):`, notifsList);
            return notifsList;
        } catch (error) {
            console.error(`Errore nel controllo delle notifiche per ${email}:`, error);
            return [];
        }
    }
}